import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, ShieldCheck, ArrowRight, XCircle, CheckCircle } from 'lucide-react';

// Página pública "Redefinir senha" (etapa 2). Aberta pelo link do WhatsApp:
// /redefinir-senha/:token. Ao montar, valida o token no backend; se válido,
// mostra os campos de nova senha (com a mesma política/checklist do Login).
function RedefinirSenha() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);   // validando token
  const [tokenValido, setTokenValido] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState('');

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [verSenha, setVerSenha] = useState(false);
  const [verConfirmar, setVerConfirmar] = useState(false);

  // Política de senha — idêntica ao Login e ao backend.
  const temMinimo = (p) => p.length >= 6;
  const temMaiuscula = (p) => /[A-Z]/.test(p);
  const temEspecial = (p) => /[!@#$&*]/.test(p);
  const senhaForte = temMinimo(novaSenha) && temMaiuscula(novaSenha) && temEspecial(novaSenha);

  // Valida o token ao abrir a página.
  useEffect(() => {
    const validar = async () => {
      try {
        const response = await fetch(`/api/reset-senha/validar/${token}`);
        const data = await response.json();
        setTokenValido(response.ok && data.valido === true);
      } catch (err) {
        setTokenValido(false);
      } finally {
        setLoading(false);
      }
    };
    validar();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');

    if (novaSenha !== confirmar) {
      setErro('As senhas não coincidem.');
      return;
    }
    if (!senhaForte) {
      setErro('A senha não atende aos requisitos.');
      return;
    }

    setEnviando(true);
    try {
      const response = await fetch('/api/reset-senha/redefinir', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, novaSenha }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Erro ao redefinir a senha.');
      setSucesso(true);
    } catch (err) {
      setErro(err.message);
    } finally {
      setEnviando(false);
    }
  };

  // Conteúdo do lado direito conforme o estado.
  const renderConteudo = () => {
    if (loading) {
      return (
        <div className="form-header" style={{ marginBottom: 0 }}>
          <h3>Validando link...</h3>
          <p>Um instante, por favor.</p>
        </div>
      );
    }

    if (!tokenValido) {
      return (
        <div className="form-header" style={{ marginBottom: 0 }}>
          <div className="icon-badge"><XCircle size={28} color="#BC0005" /></div>
          <h3>Link inválido ou expirado</h3>
          <p style={{ marginTop: 16, lineHeight: 1.6 }}>
            Este link não é mais válido. Solicite um novo na tela "Esqueci minha senha".
          </p>
          <button onClick={() => navigate('/esqueci-senha')} className="btn-primary-login" style={{ marginTop: 28 }}>
            Solicitar novo link <ArrowRight size={20} />
          </button>
        </div>
      );
    }

    if (sucesso) {
      return (
        <div className="form-header" style={{ marginBottom: 0 }}>
          <div className="icon-badge"><CheckCircle size={28} color="#BC0005" /></div>
          <h3>Senha redefinida!</h3>
          <p style={{ marginTop: 16, lineHeight: 1.6 }}>Já pode entrar com a nova senha.</p>
          <button onClick={() => navigate('/login')} className="btn-primary-login" style={{ marginTop: 28 }}>
            Ir para o login <ArrowRight size={20} />
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="form-header">
          <div className="icon-badge"><ShieldCheck size={28} color="#BC0005" /></div>
          <h3>Definir nova senha</h3>
          <p>Crie uma senha forte para sua conta</p>
        </div>

        <form onSubmit={handleSubmit} className="modern-login-form">
          <div className="password-rules">
            <div className={temMinimo(novaSenha) ? 'rule valid' : 'rule'}>
              {temMinimo(novaSenha) ? '✓' : '○'} Mín. de 6 caracteres
            </div>
            <div className={temMaiuscula(novaSenha) ? 'rule valid' : 'rule'}>
              {temMaiuscula(novaSenha) ? '✓' : '○'} 1 Letra Maiúscula
            </div>
            <div className={temEspecial(novaSenha) ? 'rule valid' : 'rule'}>
              {temEspecial(novaSenha) ? '✓' : '○'} 1 Caractere Especial (!@#$&*)
            </div>
          </div>

          {erro && <div className="error-message">{erro}</div>}

          <div className="input-group">
            <label htmlFor="novaSenha">Nova Senha</label>
            <div className="input-with-icon">
              <Lock size={20} className="input-icon" />
              <input
                type={verSenha ? 'text' : 'password'}
                id="novaSenha"
                placeholder="Sua nova senha"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                required
              />
              <button type="button" className="toggle-password" onClick={() => setVerSenha(!verSenha)}>
                {verSenha ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="confirmar">Confirmar Senha</label>
            <div className="input-with-icon">
              <Lock size={20} className="input-icon" />
              <input
                type={verConfirmar ? 'text' : 'password'}
                id="confirmar"
                placeholder="Confirme a senha"
                value={confirmar}
                onChange={(e) => setConfirmar(e.target.value)}
                required
              />
              <button type="button" className="toggle-password" onClick={() => setVerConfirmar(!verConfirmar)}>
                {verConfirmar ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn-primary-login" disabled={enviando || !senhaForte}>
            {enviando ? 'Salvando...' : <>Redefinir senha <ArrowRight size={20} /></>}
          </button>

          <div className="form-actions" style={{ justifyContent: 'center', marginTop: 20, marginBottom: 0 }}>
            <Link to="/login" className="forgot-link">Voltar ao login</Link>
          </div>
        </form>
      </>
    );
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-premium-card">
        <div className="login-brand-side">
          <div className="brand-content">
            <img src="/icon-logotipo.png" alt="Grupo Correia" className="brand-logo" />
          </div>
        </div>
        <div className="login-form-side">
          {renderConteudo()}
        </div>
      </div>
    </div>
  );
}

export default RedefinirSenha;
